import { todayWithSystemTimeZone } from '@magicdoorinc/date';
import IconAccountsPayable from '~/assets/images/reports/accountsPayable.svg';
import IconBalanceSheet from '~/assets/images/reports/balanceSheet.svg';
import IconBankAccountActivity from '~/assets/images/reports/bankAccountActivity.svg';
import IconCashFlow from '~/assets/images/reports/cashFlow.svg';
import IconDelinquency from '~/assets/images/reports/delinquency.svg';
import IconGeneralLedger from '~/assets/images/reports/generalLedger.svg';
import IconIncomeStatement from '~/assets/images/reports/incomeStatement.svg';
import IconLateFeesCharged from '~/assets/images/reports/lateFeesCharged.svg';
import IconLeaseUnpaidBill from '~/assets/images/reports/leaseUnpaidBill.svg';
import IconMagicTags from '~/assets/images/reports/magicTags.svg';
import IconOwnerDirectory from '~/assets/images/reports/ownerDirectory.svg';
import IconOwnerStatement from '~/assets/images/reports/ownerStatement.svg';
import IconPrepaidRent from '~/assets/images/reports/prepaidRent.svg';
import IconPropertyDirectory from '~/assets/images/reports/propertyDirectory.svg';
import IconPropertyReserve from '~/assets/images/reports/propertyReserve.svg';
import IconRentRoll from '~/assets/images/reports/rentRoll.svg';
import IconTenantDirectory from '~/assets/images/reports/tenantDirectory.svg';
import { MagicDoorFeature } from '~/swagger/portal/Api';

export type ReportFilters = Record<string, string | boolean>;

export type ReportAction = {
  id: string;
  labelKey: string;
  path?: string;
  filters?: () => ReportFilters;
};

export type ReportDefinition = {
  labelKey: string;
  path: string;
  icon: string;
  actions: ReportAction[];
  feature?: MagicDoorFeature;
  searchKeywords?: string[];
};

const today = () => todayWithSystemTimeZone().format('YYYY-MM-DD');

const todayRange = (): ReportFilters => ({ start: today(), end: today() });

export const lastMonthRange = (): ReportFilters => ({
  start: todayWithSystemTimeZone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
  end: todayWithSystemTimeZone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
});

export const REPORTS: ReportDefinition[] = [
  {
    labelKey: 'Balance sheet',
    path: '/reports/balance-sheet',
    icon: IconBalanceSheet,
    actions: [{ id: 'report-balance-sheet', labelKey: 'Balance sheet' }],
  },
  {
    labelKey: 'Cash flow',
    path: '/reports/cash-flow',
    icon: IconCashFlow,
    searchKeywords: ['cash flow statement'],
    actions: [
      { id: 'report-cash-flow', labelKey: 'Cash flow' },
      { id: 'report-cash-flow-by-month', labelKey: 'Cash flow by month', filters: () => ({ groupBy: 'month' }) },
      { id: 'report-cash-flow-by-property', labelKey: 'Cash flow by property', filters: () => ({ groupBy: 'property' }) },
      { id: 'report-cash-flow-by-quarter', labelKey: 'Cash flow by quarter', filters: () => ({ groupBy: 'quarter' }) },
      { id: 'report-cash-flow-by-year', labelKey: 'Cash flow by year', filters: () => ({ groupBy: 'year' }) },
    ],
  },
  {
    labelKey: 'Income statement',
    path: '/reports/income-statement',
    icon: IconIncomeStatement,
    searchKeywords: ['income report', 'profit and loss', 'profit & loss', 'p&l'],
    actions: [
      { id: 'report-income-statement', labelKey: 'Income statement', filters: lastMonthRange },
      { id: 'report-income-statement-today', labelKey: 'Income statement for today', filters: todayRange },
    ],
  },
  {
    labelKey: 'General ledger',
    path: '/reports/general-ledger',
    icon: IconGeneralLedger,
    searchKeywords: ['gl'],
    actions: [
      { id: 'report-general-ledger', labelKey: 'General ledger' },
      { id: 'report-general-ledger-today', labelKey: 'General ledger for today', filters: todayRange },
    ],
  },
  {
    labelKey: 'Owner statement',
    path: '/reports/owner-statement',
    icon: IconOwnerStatement,
    feature: MagicDoorFeature.CompanyLedger,
    actions: [{ id: 'report-owner-statement', labelKey: 'Owner statement' }],
  },
  {
    labelKey: 'Owner directory',
    path: '/reports/owner-directory',
    icon: IconOwnerDirectory,
    feature: MagicDoorFeature.CompanyLedger,
    actions: [{ id: 'report-owner-directory', labelKey: 'Owner directory' }],
  },
  {
    labelKey: 'Rent roll',
    path: '/reports/rent-roll',
    icon: IconRentRoll,
    actions: [{ id: 'report-rent-roll', labelKey: 'Rent roll' }],
  },
  {
    labelKey: 'Property directory',
    path: '/reports/property-directory',
    icon: IconPropertyDirectory,
    actions: [
      { id: 'report-inventory', labelKey: 'Inventory', path: '/reports/inventory' },
      { id: 'report-property-directory', labelKey: 'Property directory' },
      { id: 'report-property-directory-active', labelKey: 'Property directory for active', filters: () => ({ active: true }) },
      { id: 'report-property-directory-inactive', labelKey: 'Property directory for inactive', filters: () => ({ active: false }) },
    ],
  },
  {
    labelKey: 'Property reserve report',
    path: '/reports/property-reserve',
    icon: IconPropertyReserve,
    actions: [{ id: 'report-property-reserve', labelKey: 'Property reserve report' }],
  },
  {
    labelKey: 'Tenant directory',
    path: '/reports/tenant-directory',
    icon: IconTenantDirectory,
    actions: [
      { id: 'report-tenant-directory', labelKey: 'Tenant directory' },
      {
        id: 'report-tenant-directory-without-lease',
        labelKey: 'Tenant directory without active lease',
        filters: () => ({ includeNoActiveLease: true }),
      },
      {
        id: 'report-tenant-directory-with-lease',
        labelKey: 'Tenant directory with active lease',
        filters: () => ({ includeNoActiveLease: false }),
      },
    ],
  },
  {
    labelKey: 'Accounts payable',
    path: '/reports/accounts-payable',
    icon: IconAccountsPayable,
    searchKeywords: ['ap', 'unpaid bills'],
    actions: [{ id: 'report-accounts-payable', labelKey: 'Accounts payable' }],
  },
  {
    labelKey: 'Bank account activity',
    path: '/reports/bank-account-activity',
    icon: IconBankAccountActivity,
    actions: [{ id: 'report-bank-account-activity', labelKey: 'Bank account activity' }],
  },
  {
    labelKey: 'Lease unpaid bills',
    path: '/reports/lease-unpaid-bills',
    icon: IconLeaseUnpaidBill,
    actions: [{ id: 'report-lease-unpaid-bills', labelKey: 'Lease unpaid bills' }],
  },
  {
    labelKey: 'Delinquency report',
    path: '/reports/delinquency-report',
    icon: IconDelinquency,
    searchKeywords: ['past due', 'arrears'],
    actions: [{ id: 'report-delinquency-report', labelKey: 'Delinquency report' }],
  },
  {
    labelKey: 'Prepaid rent report',
    path: '/reports/prepaid-rent',
    icon: IconPrepaidRent,
    actions: [{ id: 'report-prepaid-rent', labelKey: 'Prepaid rent report' }],
  },
  {
    labelKey: 'Rent payments report',
    path: '/reports/rent-payments',
    icon: IconIncomeStatement,
    actions: [{ id: 'report-rent-payments', labelKey: 'Rent payments report' }],
  },
  {
    labelKey: 'Late fees charged report',
    path: '/reports/late-fees',
    icon: IconLateFeesCharged,
    actions: [
      { id: 'report-late-fees', labelKey: 'Late fees charged report' },
      { id: 'report-late-fees-with-paid', labelKey: 'Late fees including paid', filters: () => ({ includePaid: 'true' }) },
      {
        id: 'report-late-fees-with-manual',
        labelKey: 'Late fees including manual charges',
        filters: () => ({ includeManualCharges: 'true' }),
      },
    ],
  },
  {
    labelKey: 'Magic tags',
    path: '/reports/magic-tags/leases',
    icon: IconMagicTags,
    searchKeywords: ['magic tags'],
    actions: [{ id: 'report-lease-magic-tags', labelKey: 'Lease magic tags' }],
  },
];
